import { FocusEvent, ReactNode } from 'react';

import { DateRangePresetCategory } from '@/modules/common/components/molecules/date/date-range-picker-v2';
import { PaginatedResponse } from '@/modules/common/types/pagination-types';
import { RangeMode } from '@/types/filters';

/**
 * Filter option for autocomplete-type filters
 */
export interface TableFilterOption {
  label: string;
  value: string;
  color?: string;
  avatar?: string | null;
  /** Optional secondary metadata (e.g. a member status) for custom label renderers */
  status?: string | null;
  /** Effective date of the nearest future change that archives this entity (projects/short). */
  nextArchiveDate?: string | null;
  /** Effective date of the nearest future change that unarchives this entity (projects/short). */
  nextUnarchiveDate?: string | null;
}

/**
 * Base filter configuration
 */
export interface BaseTableFilterConfig {
  /** Unique filter identifier (used in URL params) */
  name: string;
  /** Display label for the filter */
  label: string;
  /** Whether this filter can be saved */
  saveable?: boolean;
  /** Sort order in dropdown */
  order?: number;
  /**
   * Optional section group label. Consecutive filters sharing the same group
   * are rendered under a section header in the dropdown, with their items
   * connected by a left vertical line. Filters without a group render plainly.
   */
  group?: string;
  /**
   * Optional icon rendered next to the group header. Taken from the first
   * filter of each group; set it on that filter (or every filter in the group).
   */
  groupIcon?: ReactNode;
  /**
   * When true, the filter is locked: its dropdown category is non-interactive
   * and its chip cannot be removed. The value still shows (in the dropdown and
   * as a chip) so it reflects reality — used e.g. to pin a status filter while a
   * mode constrains it.
   */
  disabled?: boolean;
  /**
   * Tooltip shown on the locked row explaining WHY it is disabled
   * (e.g. "Requires a date range filter"). Only used when `disabled` is true.
   */
  disabledHint?: string;
}

/**
 * Static autocomplete filter with predefined options
 */
export interface StaticAutocompleteFilterConfig extends BaseTableFilterConfig {
  type: 'autocomplete';
  options: TableFilterOption[];
  /** Enable search within options */
  withSearch?: boolean;
  /** Custom label renderer */
  renderLabel?: (value: string, option: TableFilterOption) => ReactNode;
  /** Show colored dot indicator */
  displayDot?: boolean;
}

/**
 * Dynamic autocomplete filter that loads options from API
 */
export interface DynamicAutocompleteFilterConfig extends BaseTableFilterConfig {
  type: 'dynamic-autocomplete';
  /** Function to load options - returns items with { id, name } or { label, value } */

  loadOptions: (params: {
    searchString?: string;
    cursor?: number | null;
    pageSize?: number;
  }) => Promise<PaginatedResponse<any>>;
  /** Enable search */
  withSearch?: boolean;
  /** Custom label renderer */
  renderLabel?: (value: string, option: TableFilterOption) => ReactNode;
  /** Minimum characters to trigger search */
  minSearchChars?: number;
  /** Debounce delay for search */
  debounceMs?: number;
  /**
   * Name of another filter this one depends on. When set, the dependent filter's
   * active value is passed into `loadOptions` (e.g. City options scoped by Country).
   */
  dependsOn?: string;
  /**
   * Static options pinned above the loaded ones (e.g. a "No department" option
   * with the FILTER_OPTION_NO_VALUE sentinel). Hidden while a search term is
   * active — searching implies looking for a concrete entity.
   */
  topOptions?: TableFilterOption[];
}

/**
 * Range mode filter (More than / Less than / Equal to + numeric amount).
 * Value is stored as a single `"mode:amount"` string (e.g. "more:50"), with an
 * optional `:strict` suffix when the including toggle is off — see
 * utils/range-mode-filter-value.ts for the parse/serialize helpers.
 */
export interface RangeModeFilterConfig extends BaseTableFilterConfig {
  type: 'rangeMode';
  modes: Array<{ label: string; value: RangeMode }>;
  /** Unit suffix shown next to the amount input (e.g. "%", "h/day") */
  unit?: string;
  placeholder?: string;
  /** Optional min for the amount input (defaults to 0) */
  min?: number;
  /** Optional max for the amount input (omit to leave unbounded) */
  max?: number;
  /**
   * Shows an "Including the value" checkbox for the More/Less modes (checked by
   * default → >= / <=; unchecked → strict > / <). Equal is always inclusive, so
   * the checkbox is hidden while Equal is selected.
   */
  inclusiveToggle?: boolean;
}

/**
 * Boolean toggle filter rendered as a switch row inside the dropdown.
 * Value is the string `'true'` when on.
 */
export interface SwitchFilterConfig extends BaseTableFilterConfig {
  type: 'switch';
  /**
   * When true, this switch is excluded from the active-filter count badge AND
   * from the filter chips, and persists an explicit `'false'` when toggled off
   * (so an absent value can default to ON). Used by the "Hide archived" toggle.
   */
  hiddenFromSummary?: boolean;
}

/**
 * Search/text input filter
 */
export interface SearchFilterConfig extends BaseTableFilterConfig {
  type: 'search';
  placeholder?: string;
  /** Minimum characters to trigger filter */
  minCharacters?: number;
}

/**
 * Date range filter. Value is stored as a single 'yyyy-MM-dd:yyyy-MM-dd' string
 * (see utils/date-range-filter-value.ts — the separator must stay comma-free).
 */
export interface DateRangeFilterConfig extends BaseTableFilterConfig {
  type: 'dateRange';
  placeholder?: string;
  /** Preset categories to omit in the submenu (passes through to PresetCategoriesList) */
  hiddenPresetCategories?: DateRangePresetCategory[];
}

/**
 * Union type of all filter configurations
 */
export type TableFilterConfig =
  | StaticAutocompleteFilterConfig
  | DynamicAutocompleteFilterConfig
  | SearchFilterConfig
  | DateRangeFilterConfig
  | RangeModeFilterConfig
  | SwitchFilterConfig;

/**
 * Filter state - values for each filter
 */
export type TableFilterState = Record<string, string | string[]>;

/**
 * Return type of useTableFilters hook
 */
export interface UseTableFiltersReturn {
  activeFilters: TableFilterState;
  activeFiltersCount: number;
  hasActiveFilters: boolean;
  setFilter: (name: string, value: string | string[]) => void;
  clearFilter: (name: string) => void;
  clearAllFilters: () => void;
  applyFilters: (filters: TableFilterState) => void;
  removeFilterValue: (name: string, valueToRemove?: string) => void;
  getFilterValue: (name: string) => string[];
}

/**
 * Saved filter entity
 */
export interface SavedFilter {
  id: string;
  name: string;
  filters: TableFilterState;
  access: FilterAccess;
  createdBy?: {
    id: number;
    name: string;
  };
  createdAt?: string;
}

/**
 * Props for the main TableFilters component
 */
export interface TableFiltersProps {
  /** Filter configurations */
  filtersConfig: TableFilterConfig[];
  /** Unique table identifier for saved filters */
  tableId: string;
  /** Show save button (default: true) */
  showSaveButton?: boolean;
  /** Show clear button (default: true) */
  showClearButton?: boolean;
  /** Custom class name */
  className?: string;
  /** External filter state from useTableFilters hook (controlled mode) */
  filterState?: UseTableFiltersReturn;
  /**
   * Veto callback fired before a filter change is applied. Return `false` to
   * block the change (e.g. to prompt an unsaved-changes guard first).
   */
  onFilterValueChange?: (name: string, value: string | string[]) => boolean | void;
  /**
   * Fired when the search input gains focus. Return `false` to blur it
   * immediately (e.g. to prompt an unsaved-changes guard first).
   */
  onSearchFocus?: (event: FocusEvent<HTMLInputElement>) => boolean | void;
  /**
   * Controlled open state for the filter dropdown. When provided, the component
   * uses this value instead of its own internal state — lets callers close the
   * dropdown programmatically (e.g. before showing an unsaved-changes modal).
   */
  dropdownOpen?: boolean;
  /** Called when the dropdown's open state changes */
  onDropdownOpenChange?: (open: boolean) => void;
  /**
   * Rendered as the first item of the wrapping controls row (before the filter
   * button and chips). Lets callers put an external search input inside the row
   * so wrapped chips flow under it instead of under the filter button.
   */
  leadingControls?: ReactNode;
}

/**
 * Props for filter dropdown
 */
export interface FilterDropdownProps {
  filtersConfig: TableFilterConfig[];
  activeFilters: TableFilterState;
  onFilterChange: (name: string, value: string | string[]) => void;
  onClearFilter: (name: string) => void;
  savedFilters?: SavedFilter[];
  onApplySavedFilter?: (filter: SavedFilter) => void;
  /** Controlled open state of the dropdown (optional — falls back to internal state) */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Name of a filter whose submenu should auto-open (e.g. when its chip is clicked) */
  focusedFilterName?: string | null;
  /** Called once the focused filter's submenu has been opened */
  onFocusedFilterHandled?: () => void;
}

/**
 * Props for filter chips
 */
export interface FilterChipsProps {
  filtersConfig: TableFilterConfig[];
  activeFilters: TableFilterState;
  onRemoveFilter: (name: string, value?: string) => void;
  onClearAll?: () => void;
  /** Called when a chip body (not its remove button) is clicked — opens that filter */
  onChipClick?: (name: string) => void;
}

export interface FilterCategoryItemProps {
  config: TableFilterConfig;
  activeValues: string[];
  onFilterChange: (name: string, value: string[]) => void;
  onClearFilter: (name: string) => void;
  /** All active filter values — used to resolve `dependsOn` for dynamic filters */
  activeFilters?: TableFilterState;
  /** When true, the category's submenu opens automatically (e.g. after a chip click) */
  forceOpen?: boolean;
  /** Called once the forced-open has been applied, so the parent can reset its focus state */
  onForceOpenHandled?: () => void;
}

/**
 * Props for a single filter category item in the dropdown. Extends the shared
 * submenu props with controlled open state.
 *
 * The parent dropdown owns a single "which submenu is open" value so only one
 * submenu is ever open at a time — this prevents multiple submenus stacking open
 * when pointer/focus events are missed (e.g. on browser tab switch).
 */
export interface FilterCategoryItemControlProps extends FilterCategoryItemProps {
  /** Controlled open state of this submenu */
  open: boolean;
  /** Called when this submenu requests to open or close */
  onOpenChange: (open: boolean) => void;
}

export enum FilterAccess {
  Public = 'public',
  Private = 'private',
}
